import {
  ActionsDropdownComponent,
  type DropdownItems,
} from '@myre/ui-primitive-actions-dropdown';
import {
  ButtonComponent,
  ButtonHeight,
  ButtonSubTheme,
  ButtonTheme,
} from '@myre/ui-primitive-components';

import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  input,
  isSignal,
  signal,
} from '@angular/core';
import { castArray, compact } from 'lodash-es';

import { type ActionButton } from './interfaces';

/**
 * Action group component is a container for buttons.
 * It can display primary buttons, secondary buttons, and options buttons.
 */
@Component({
  selector: 'myre-action-group',
  templateUrl: './action-group.component.html',
  styleUrls: ['./action-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ActionsDropdownComponent, NgTemplateOutlet, ButtonComponent],
})
export class ActionGroupComponent {
  /**
   * Primary buttons are colored buttons that are displayed on the right of the line.
   * Default color is blue
   */
  @Input()
  public set primaryButtons(
    primaryButtons: readonly ActionButton[] | ActionButton,
  ) {
    this._primaryButtons.set(compact(castArray(primaryButtons)));
  }

  /**
   * Secondary buttons are displayed on the right of the line, before primary buttons.
   * They are not colored.
   */
  @Input()
  public set secondaryButtons(
    secondaryButtons: readonly ActionButton[] | ActionButton,
  ) {
    this._secondaryButtons.set(compact(castArray(secondaryButtons)));
  }

  /**
   * Options buttons are displayed in a dropdown menu after a click on the three dots button.
   *
   */
  public readonly optionsButtons = input<DropdownItems | null>(null);

  protected readonly _primaryButtons = signal<readonly ActionButton[]>([]);
  protected readonly _secondaryButtons = signal<readonly ActionButton[]>([]);

  protected readonly _ButtonTheme = ButtonTheme;
  protected readonly _ButtonSubTheme = ButtonSubTheme;
  protected readonly _ButtonHeight = ButtonHeight;

  protected _track(index: number, button: ActionButton): string {
    return `${button.label}-${button.icon}-${index}`;
  }

  protected _actionButton(button: ActionButton): ActionButton {
    return button;
  }

  protected readonly _isSignal = isSignal;
}
